import React, { useState } from 'react';
import { ChevronRight, Sun, Moon, Bell, BellOff, RefreshCw, LogOut, Edit3, Check, X as XIcon } from 'lucide-react';
import { signOut } from '../lib/auth.js';
import { updateProfile } from '../lib/profile.js';
import { ProfilePicture } from '../components/ProfilePicture.jsx';

export function SettingsPage({ theme, profile, partner, pair, themeMode, onThemeChange, onProfileUpdate, onShowOnboarding, onInvitePartner }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(profile?.name || '');
  const [saving, setSaving] = useState(false);

  async function handleNameSave() {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      const updated = await updateProfile(profile.id, { name: name.trim() });
      onProfileUpdate(updated);
      setEditingName(false);
    } catch (e) {
      alert('Kunne ikke gemme: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleNotifToggle(field) {
    try {
      const updates = { [field]: !profile[field] };
      const updated = await updateProfile(profile.id, updates);
      onProfileUpdate(updated);
    } catch (e) {
      alert('Kunne ikke gemme: ' + e.message);
    }
  }

  async function handleThemeToggle() {
    const newMode = themeMode === 'dark' ? 'light' : 'dark';
    try {
      const updated = await updateProfile(profile.id, { theme_mode: newMode });
      onProfileUpdate(updated);
      onThemeChange(newMode);
    } catch (e) {
      alert('Kunne ikke gemme: ' + e.message);
    }
  }

  async function handleLogout() {
    await signOut();
    window.location.href = '/';
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 18px', fontSize: 22, fontWeight: 700 }}>Indstillinger</h1>

      {/* Profile */}
      <Section theme={theme} title="Profil">
        <div style={{ padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            <ProfilePicture user={profile} size={56} theme={theme} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingName ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleNameSave()}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      background: theme.bg,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      color: theme.text,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    autoFocus
                  />
                  <button onClick={handleNameSave} disabled={saving} style={miniBtn(theme, 'primary')}>
                    <Check size={14} />
                  </button>
                  <button onClick={() => { setEditingName(false); setName(profile.name); }} style={miniBtn(theme, 'ghost')}>
                    <XIcon size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <div style={{
                    fontSize: 16, fontWeight: 600, color: theme.text,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {profile?.name}
                    <button onClick={() => setEditingName(true)} style={{
                      background: 'transparent', border: 'none',
                      color: theme.textMuted, cursor: 'pointer', padding: 2,
                    }}>
                      <Edit3 size={13} />
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
                    {profile?.email}
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={{ fontSize: 11, color: theme.textDim, lineHeight: 1.5 }}>
            Profilbillede hentes fra din {profile?.authProvider === 'facebook' ? 'Facebook' : 'Google'}-konto.
          </div>
        </div>
      </Section>

      {/* Partner */}
      <Section theme={theme} title="Partner">
        {pair ? (
          <div style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <ProfilePicture user={partner} size={42} theme={theme} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{partner?.name}</div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
                Parret med dig
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: 14 }}>
            <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 10, lineHeight: 1.5 }}>
              Du er solo lige nu. Inviter din partner for at dele opgaver, indkøb og kærlighedspoint.
            </div>
            <button onClick={onInvitePartner} style={{
              width: '100%',
              padding: '11px 14px',
              borderRadius: 10,
              background: theme.coral,
              border: 'none',
              color: 'white',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}>
              Inviter partner
            </button>
          </div>
        )}
      </Section>

      {/* Theme */}
      <Section theme={theme} title="Udseende">
        <Row theme={theme} icon={themeMode === 'dark' ? Moon : Sun} label="Tema" desc={themeMode === 'dark' ? 'Mørkt tema' : 'Lyst tema'} onClick={handleThemeToggle}>
          <Toggle theme={theme} active={themeMode === 'dark'} />
        </Row>
      </Section>

      {/* Notifications */}
      <Section theme={theme} title="Notifikationer">
        <Row theme={theme} icon={profile?.notificationsEnabled ? Bell : BellOff} label="Notifikationer aktiveret" onClick={() => handleNotifToggle('notifications_enabled')}>
          <Toggle theme={theme} active={!!profile?.notificationsEnabled} />
        </Row>
        <Divider theme={theme} />
        <Row theme={theme} label="Når jeg får en ny opgave" onClick={() => handleNotifToggle('notify_on_new_task')}>
          <Toggle theme={theme} active={!!profile?.notifyOnNewTask} />
        </Row>
        <Divider theme={theme} />
        <Row theme={theme} label="Når partner løser en opgave" onClick={() => handleNotifToggle('notify_on_task_completed')}>
          <Toggle theme={theme} active={!!profile?.notifyOnTaskCompleted} />
        </Row>
      </Section>

      {/* Misc */}
      <Section theme={theme} title="Andet">
        <Row theme={theme} icon={RefreshCw} label="Vis velkomstbesked igen" desc="Se introduktionen til Tag Team" onClick={onShowOnboarding}>
          <ChevronRight size={14} color={theme.textDim} />
        </Row>
      </Section>

      {/* Log out */}
      <button onClick={handleLogout} style={{
        width: '100%',
        padding: '14px 16px',
        background: 'transparent',
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        color: theme.textMuted,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 8,
      }}>
        <LogOut size={15} /> Log ud
      </button>

      <div style={{
        textAlign: 'center',
        marginTop: 28,
        fontSize: 11,
        color: theme.textDim,
      }}>
        Tag Team · v0.4.0
      </div>
    </div>
  );
}

function Section({ theme, title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h3 style={{
        margin: '0 0 8px', fontSize: 11, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 1,
        color: theme.textMuted,
      }}>{title}</h3>
      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.borderSubtle}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}>
        {children}
      </div>
    </div>
  );
}

function Row({ theme, icon: Icon, label, desc, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      width: '100%',
      background: 'transparent',
      border: 'none',
      color: theme.text,
      padding: '12px 14px',
      cursor: onClick ? 'pointer' : 'default',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      textAlign: 'left',
    }}>
      {Icon && <Icon size={16} color={theme.textMuted} />}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{desc}</div>}
      </div>
      {children}
    </button>
  );
}

function Toggle({ theme, active }) {
  return (
    <div style={{
      width: 36, height: 20,
      background: active ? theme.coral : theme.border,
      borderRadius: 999,
      position: 'relative',
      transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute',
        top: 2, left: active ? 18 : 2,
        width: 16, height: 16,
        background: 'white',
        borderRadius: '50%',
        transition: 'left 0.2s',
      }} />
    </div>
  );
}

function Divider({ theme }) {
  return <div style={{ height: 1, background: theme.borderSubtle, margin: '0 14px' }} />;
}

function miniBtn(theme, variant) {
  const base = {
    padding: 7,
    borderRadius: 6,
    cursor: 'pointer',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  if (variant === 'primary') return { ...base, background: theme.coral, color: 'white' };
  return { ...base, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}` };
}
